import * as React from 'react'
import uniq from 'lodash/uniq'
import {type PolyfilledEvent} from '@frontrow/console/src/polyfills'
import {Button} from '@frontrow/console/src/components/button'
import {Label} from '@frontrow/console/src/components/ui/label'
import Input from '@frontrow/console/src/entities/components/base/input'
import {Spinner} from '@frontrow/console/src/components/spinner'
import {Languages, type LangT} from '@frontrow/console/src/entities/helpers/languages'
import {LanguageDropdown} from '@frontrow/console/src/components/teacher-language-dropdown/index'

import {type BasicSchoolAdminT} from '@frontrow/console/src/models/school-admin'
import FormField from '@frontrow/console/src/components/common/form-field'
import * as style from '@frontrow/console/src/components/pages/school-admins-page/school-admin-form/school-admin-form.module.scss'

type Props = {
  onCancel: () => void
  onSubmit: (
    newSchoolAdminData: BasicSchoolAdminT,
    schoolIds: Array<number>,
    addAnother: boolean
  ) => void
  resetInput: boolean
  isLoading: boolean
}

type SchoolAdminPlaceholders = {
  firstName: string
  lastName: string
  position: string
  email: string
  schoolIdsStr: string
  districtId: string
}

function getCreatePlaceholders(): SchoolAdminPlaceholders {
  return {
    firstName: 'Jane',
    lastName: 'Doe',
    position: 'Principal',
    email: 'jane-doe@freckle.com',
    schoolIdsStr: '1,2',
    districtId: '1'
  }
}

type TextFieldT = 'firstName' | 'lastName' | 'email' | 'position' | 'schoolIdsStr'
type NumFieldT = 'districtId'

export default function SchoolAdminCreateForm(props: Props): React.ReactElement {
  const {onCancel, onSubmit, resetInput, isLoading} = props
  const formRef = React.useRef<HTMLFormElement | null>(null)

  const [firstName, setFirstName] = React.useState<string>('')
  const [lastName, setLastName] = React.useState<string>('')
  const [position, setPosition] = React.useState<string>('')
  const [email, setEmail] = React.useState<string>('')
  const [confirmed, setConfirmed] = React.useState<boolean>(true)
  const [districtId, setDistrictId] = React.useState<number | null | undefined>(null)
  const [schoolIdsStr, setSchoolIdsStr] = React.useState<string>('')
  const [language, setLanguage] = React.useState<LangT>(Languages.English)

  React.useEffect(() => {
    if (resetInput === true) {
      resetForNewEntry()
    }
  }, [resetInput])

  const resetForNewEntry = () => {
    //purposely only partially reset
    setFirstName('')
    setLastName('')
    setPosition('')
    setEmail('')
  }

  const prepAdminData = (): {
    admin: BasicSchoolAdminT
    restSchoolIds: Array<number>
  } => {
    const state = {
      firstName,
      lastName,
      position,
      email,
      confirmed,
      districtId,
      schoolIdsStr,
      language
    }
    const {schoolIdsStr: schoolIdsStrValue, ...restAdmin} = state
    const [firstSchoolId, ...restSchoolIds] = uniq(getSchoolIdsFromStr(schoolIdsStrValue))
    const admin =
      schoolIdsStrValue.length > 0 ? {...restAdmin, schoolId: firstSchoolId} : {...restAdmin}

    return {admin, restSchoolIds}
  }

  const handleSubmit = (e: PolyfilledEvent) => {
    e.preventDefault()
    const {admin, restSchoolIds} = prepAdminData()
    onSubmit(admin, restSchoolIds, false)
  }

  const onClickSaveAndAddAnother = () => {
    if (formRef.current !== null && formRef.current !== undefined) {
      if (formRef.current.reportValidity()) {
        const {admin, restSchoolIds} = prepAdminData()
        onSubmit(admin, restSchoolIds, true)
      }
    }
  }

  const createFieldOnChangeText = (field: TextFieldT): ((val: string) => void) => {
    return (newValue: string) => {
      switch (field) {
        case 'firstName':
          setFirstName(newValue)
          break
        case 'lastName':
          setLastName(newValue)
          break
        case 'email':
          setEmail(newValue)
          break
        case 'position':
          setPosition(newValue)
          break
        case 'schoolIdsStr':
          setSchoolIdsStr(newValue)
          break
        default:
          break
      }
    }
  }

  const createFieldOnChangeNum = (field: NumFieldT): ((val: string) => void) => {
    return (newValue: string) => {
      switch (field) {
        case 'districtId':
          setDistrictId(parseFloat(newValue))
          break
        default:
          break
      }
    }
  }

  const onToggleConfirmed = () => {
    setConfirmed(prev => !prev)
  }

  const placeholders = getCreatePlaceholders()

  if (isLoading) {
    return <Spinner />
  }

  return (
    <form onSubmit={handleSubmit} action="" ref={formRef}>
      <div className={style.schoolAdminFormContainer}>
        <p className="w-full text-xs text-muted-text mb-2">* is required</p>
        <div className={style.schoolAdminDetailsContainer}>
          <FormField
            wrapperStyle={style.schoolAdminGenericFieldContainer}
            label="First Name"
            required
          >
            <Input
              value={firstName}
              type="text"
              required
              placeholder={placeholders.firstName}
              onChange={createFieldOnChangeText('firstName')}
            />
          </FormField>
          <FormField
            wrapperStyle={style.schoolAdminGenericFieldContainer}
            label="Last Name"
            required
          >
            <Input
              value={lastName}
              type="text"
              required
              placeholder={placeholders.lastName}
              onChange={createFieldOnChangeText('lastName')}
            />
          </FormField>
          <FormField
            wrapperStyle={style.schoolAdminGenericFieldContainer}
            label="Position"
            required
          >
            <Input
              value={position}
              type="text"
              required
              placeholder={placeholders.position}
              onChange={createFieldOnChangeText('position')}
            />
          </FormField>
          <FormField wrapperStyle={style.schoolAdminGenericFieldContainer} label="Email" required>
            <Input
              value={email}
              type="text"
              required
              placeholder={placeholders.email}
              onChange={createFieldOnChangeText('email')}
            />
          </FormField>
          <FormField
            wrapperStyle={style.schoolAdminGenericFieldContainer}
            label="Language"
            required
          >
            <LanguageDropdown
              updateMode={{tag: 'component-is-passthrough'}}
              labels={{loadingText: 'Loading'}}
              selectedLanguage={language}
              onChange={setLanguage}
            />
          </FormField>
        </div>
        <div className={style.schoolAdminDetailsContainer}>
          <FormField wrapperStyle={style.schoolAdminGenericFieldContainer} label="School Ids">
            <Input
              value={schoolIdsStr}
              type="text"
              placeholder={placeholders.schoolIdsStr}
              onChange={createFieldOnChangeText('schoolIdsStr')}
              className={isValidSchoolIdsString(schoolIdsStr) ? '' : style.invalidField}
            />
          </FormField>
          <FormField wrapperStyle={style.schoolAdminGenericFieldContainer} label="District Id">
            <Input
              value={districtId === null || districtId === undefined ? '' : districtId}
              type="number"
              placeholder={placeholders.districtId}
              onChange={createFieldOnChangeNum('districtId')}
            />
          </FormField>
          <div className="w-full flex items-center gap-2 mb-3">
            <input
              id="confirmed-checkbox"
              type="checkbox"
              checked={confirmed}
              onChange={onToggleConfirmed}
              className="h-4 w-4 rounded border border-input accent-primary cursor-pointer"
            />
            <Label htmlFor="confirmed-checkbox" addClass="cursor-pointer font-normal text-body">
              Confirmed?
            </Label>
          </div>
        </div>
      </div>
      <div className="flex justify-end gap-2 mt-4">
        <Button style="secondary" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit" disabled={!isValidSchoolIdsString(schoolIdsStr)}>
          Save Admin
        </Button>
        <Button onClick={onClickSaveAndAddAnother} disabled={!isValidSchoolIdsString(schoolIdsStr)}>
          Save and Add Another
        </Button>
      </div>
    </form>
  )
}

function isValidSchoolIdsString(schoolIdsStr: string): boolean {
  return schoolIdsStr.length > 0
    ? schoolIdsStr.split(',').every(idStr => {
        const trimmedStr = idStr.trim()
        return /^\d+$/.test(trimmedStr)
      })
    : true
}

function getSchoolIdsFromStr(schoolIdsStr: string): Array<number> {
  if (!isValidSchoolIdsString(schoolIdsStr)) {
    throw new Error('Unable to extract schoolIds from invalid input')
  }

  return schoolIdsStr.split(',').map(idStr => parseInt(idStr, 10))
}
